import { Injectable, signal, computed } from '@angular/core';
import { Language } from 'src/app/master-data/master-data.model';

export type TranslationStatus = 'none' | 'partial' | 'complete';

@Injectable({ providedIn: 'root' })
export class TranslationSettingsService {
  private readonly _selectedLanguages = signal<Language[]>([]);
  readonly selectedLanguages = this._selectedLanguages.asReadonly();

  readonly selectedLocales = computed(() =>
    this._selectedLanguages().map(lang => lang.locale)
  );

  setSelectedLanguages(languages: Language[]): void {
    this._selectedLanguages.set(languages);
  }

  toggleLanguage(language: Language): void {
    const current = this._selectedLanguages();
    const exists = current.some(l => l.locale === language.locale);

    this._selectedLanguages.set(
      exists
        ? current.filter(l => l.locale !== language.locale)
        : [...current, language]
    );
  }

  isSelected(locale: string): boolean {
    return this._selectedLanguages().some(l => l.locale === locale);
  }

  /**
   * Rehydrate the selection from the persisted tenant config
   * (Configuration -> Languages, config key "languages", a comma-joined
   * list of locale codes) plus the full list of available languages.
   */
  loadFromLocales(selectedLocales: string[], allLanguages: Language[]): void {
    this._selectedLanguages.set(
      allLanguages.filter(lang => selectedLocales.includes(lang.locale))
    );
  }

  /**
   * Whether an item's translations cover none, some, or all of the
   * currently selected tenant languages.
   */
  getTranslationStatus(translations?: Record<string, string>): TranslationStatus {
    const locales = this.selectedLocales();
    if (locales.length === 0) {
      return 'none';
    }

    const filledCount = locales.filter(locale => !!translations?.[locale]?.trim()).length;
    if (filledCount === 0) {
      return 'none';
    }
    return filledCount === locales.length ? 'complete' : 'partial';
  }
}
